// Points scoring system for the Bible trivia game

export interface ScoreCalculation {
  basePoints: number;
  streakBonus: number;
  speedBonus: number;
  totalPoints: number;
}

// Base points per difficulty (iOS compatible scoring)
export const DIFFICULTY_POINTS = {
  easy: 10,        // iOS: 10 points
  medium: 20,      // iOS: 20 points (2x multiplier)
  hard: 30,        // iOS: 30 points (3x multiplier)
  beginner: 10,    // Same as easy
  intermediate: 20, // Same as medium
  advanced: 30,    // Same as hard (super hard)
  expert: 30,      // Map to hard equivalent
  mixed: 0         // Will be calculated per question
};

// iOS doesn't use streak bonuses - keeping minimal for compatibility
export const STREAK_BONUSES = {
  // No streak bonuses in iOS, but keeping for potential future use
};

// iOS doesn't use perfect game bonus - keeping minimal for compatibility  
export const PERFECT_GAME_BONUS = 0;

// Speed bonus based on remaining time (iOS compatible: max 5 points)
export const calculateSpeedBonus = (timeRemaining: number): number => {
  // iOS formula: min(5, timeRemaining / 3)
  return Math.min(5, Math.floor(timeRemaining / 3));
};

// Calculate streak bonus based on current streak (iOS: no streak bonuses)
export const calculateStreakBonus = (currentStreak: number): number => {
  // iOS app doesn't implement streak bonuses
  return 0;
};

// Calculate total points for a single answer
export const calculateAnswerPoints = (
  difficulty: string,
  timeRemaining: number,
  currentStreak: number,
  isCorrect: boolean
): ScoreCalculation => {
  if (!isCorrect) {
    return {
      basePoints: 0,
      streakBonus: 0,
      speedBonus: 0,
      totalPoints: 0
    };
  }

  const basePoints = DIFFICULTY_POINTS[difficulty as keyof typeof DIFFICULTY_POINTS] || 20;
  const speedBonus = calculateSpeedBonus(timeRemaining);
  const streakBonus = calculateStreakBonus(currentStreak);

  return {
    basePoints,
    streakBonus,
    speedBonus,
    totalPoints: basePoints + speedBonus + streakBonus
  };
};

// Calculate final game score (iOS compatible: no perfect game bonus)
export const calculateFinalScore = (
  totalScore: number,
  totalQuestions: number,
  correctAnswers: number
): { finalScore: number; perfectGameBonus: number } => {
  // iOS app doesn't implement perfect game bonus
  const perfectGameBonus = 0;
  
  return {
    finalScore: totalScore + perfectGameBonus,
    perfectGameBonus
  };
};

// DEPRECATED: Use biblical ranking system instead
// This function is kept for backward compatibility only
export const calculateLevel = (totalPoints: number): { level: number; progress: number; nextLevelPoints: number } => {
  console.warn('calculateLevel is deprecated. Use getRankByPoints from ranksService instead.');
  
  // Simple fallback that maps to biblical ranks
  if (totalPoints >= 30000) return { level: 15, progress: 100, nextLevelPoints: 30000 };
  if (totalPoints >= 20000) return { level: 14, progress: 75, nextLevelPoints: 30000 };
  if (totalPoints >= 15000) return { level: 13, progress: 50, nextLevelPoints: 20000 };
  if (totalPoints >= 10000) return { level: 12, progress: 25, nextLevelPoints: 15000 };
  
  // Map remaining levels to biblical rank equivalents
  const biblicalLevel = Math.min(Math.floor(totalPoints / 1000) + 1, 11);
  return { 
    level: biblicalLevel, 
    progress: ((totalPoints % 1000) / 1000) * 100,
    nextLevelPoints: (biblicalLevel * 1000)
  };
};

// DEPRECATED: Use getRankByPoints from ranksService instead
// This function is kept for backward compatibility only
export const getLevelTitle = (level: number): string => {
  console.warn('getLevelTitle is deprecated. Use getRankByPoints from ranksService instead.');
  
  // Simple fallback mapping to biblical rank names
  const biblicalTitles = [
    'Seeker', 'Believer', 'Follower', 'Servant', 'Shepherd', 
    'Elder', 'Prophet', 'Disciple', 'Apostle', 'Evangelist',
    'Teacher', 'Theologian', 'Scholar', 'Sage', 'Biblical Master'
  ];
  
  return biblicalTitles[Math.min(level - 1, biblicalTitles.length - 1)] || 'Biblical Master';
};